// The objection queue. Open items first — an unanswered objection is an agent waiting on
// a score they believe is wrong, so it stays at the top until it is resolved.
//
// Resolving happens on the evaluation itself (EvalDetail), where the recording and the
// original scores are on screen: answering an objection from a list row alone would mean
// deciding without listening to the call.
//
// The SAME screen is the agent's "İtirazlarım" (owner 2026-09-21, Task Board #122/#123:
// "call agents should see their objections in objections tab fully"): the server scopes the
// list to their own rows, so every appeal they ever filed is here with its full text, the
// reply and the outcome — not only the newest one Panom's status card hints at. What differs
// for them is the framing: no avatar/name column (every row is theirs), no brand pill (they
// have one brand), outcome wording addressed to them, and "Tümü" as the opening filter — the
// point of the tab is the history, not a work queue.
//
// The QMS specialist reads the same screen too (owner 2026-09-22, the same card corrected:
// "qms agents should see objections") — staff columns, but no resolve affordance anywhere:
// the row opens the evaluation, and the subtitle says who actually answers, so a read-only
// reader is never sent to a button the server would refuse.

import { useState } from 'react';
import * as api from '../api';
import type { Meta, PanelUser } from '../types';
import { useAsync } from '../hooks';
import { Avatar, Empty, Icon, Loading, Note, PageHead, Panel, Pill, ScorePill } from '../ui';
import { fullDateTime } from '../format';
import { navigate } from '../App';

type Filter = 'open' | 'resolved' | 'all';

export default function Objections({ user, meta, brand, onChanged }: {
  user: PanelUser; meta: Meta | null;
  /** The topbar's global brand filter; '' = every brand this user may see. */
  brand: string;
  onChanged: () => void;
}) {
  void meta; void onChanged;
  // supervisor + admin resolve; the agent only reads (engine/access.js canResolveObjections
  // vs canSeeObjection). The server is the gate — this picks the wording and the columns.
  const isAgent = user.role === 'agent';
  const canResolve = user.role === 'supervisor' || user.role === 'admin';
  const isQms = user.role === 'qms';
  const [filter, setFilter] = useState<Filter>(isAgent ? 'all' : 'open');
  const state = useAsync(
    () => api.listObjections(filter === 'all' ? undefined : filter, brand),
    [filter, brand],
  );

  const openCount = state.data?.openCount ?? 0;

  return (
    <>
      <PageHead
        icon="gavel"
        title={isAgent ? 'İtirazlarım' : 'İtirazlar'}
        subtitle={isAgent
          ? 'Yaptığın itirazlar, verilen yanıtlar ve sonuçları.'
          : canResolve
            ? 'Agent itirazlarını okuyun, gerekirse puanı güncelleyin.'
            : isQms
              ? 'Değerlendirmelerine yapılan itirazlar ve sonuçları. Yanıtlama yetkisi süpervizördedir.'
              : 'Ekibinizden gelen itirazlar ve sonuçları.'}
        right={(
          <div className="seg">
            {(['open', 'resolved', 'all'] as const).map((f) => (
              <button key={f} type="button" className={filter === f ? 'on' : ''} onClick={() => setFilter(f)}>
                {f === 'open' ? (isAgent ? 'Yanıt bekleyen' : 'Bekleyen') : f === 'resolved' ? 'Sonuçlanan' : 'Tümü'}
              </button>
            ))}
          </div>
        )}
      />

      <Panel
        icon="gavel"
        title="İtiraz listesi"
        hint={state.data ? `${openCount} açık · ${state.data.objections.length} itiraz listeleniyor` : ' '}
      >
        {state.error ? <div className="panel-body"><Note error>{state.error}</Note></div> : null}
        {state.loading && !state.data ? <Loading /> : null}
        {state.data && state.data.objections.length === 0 ? (
          <Empty>
            {filter === 'open'
              ? (isAgent ? 'Yanıt bekleyen itirazın yok.' : 'Bekleyen itiraz yok.')
              : (isAgent && filter === 'all' ? 'Henüz itiraz etmedin.' : 'Kayıt yok.')}
          </Empty>
        ) : null}
        {state.data?.objections.map((o) => {
          const open = !o.resolvedAt;
          // The agent-facing outcome wording is the one Panom already uses ("puan
          // korundu"); staff keep the queue's shorter labels.
          const outcome = open
            ? (isAgent ? 'Yanıt bekleniyor' : 'Bekliyor')
            : o.resolution === 'revised'
              ? 'Puan güncellendi'
              : (isAgent ? 'Puan korundu' : 'Reddedildi');
          return (
            <div
              key={o.id}
              className="aprow"
              role="link"
              tabIndex={0}
              onClick={() => navigate(`evaluation/${o.evaluationId}`)}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate(`evaluation/${o.evaluationId}`); }}
            >
              <div className="aprow-hd">
                {isAgent ? (
                  <Icon name={open ? 'pending_actions' : o.resolution === 'revised' ? 'task_alt' : 'rule'} />
                ) : (
                  <>
                    <Avatar name={o.agentName} kind={open ? 'accent' : o.resolution === 'revised' ? 'good' : 'neutral'} />
                    <span className="nm">{o.agentName}</span>
                  </>
                )}
                <span className="id">#{o.evaluationId}{o.callRef ? ` · ${o.callRef}` : ''}</span>
                {isAgent ? null : <Pill tone="neu">{o.brandKey}</Pill>}
                <ScorePill score={o.finalScore} hasCritical={o.hasCritical} />
                <Pill tone={open ? 'warn' : o.resolution === 'revised' ? 'good' : 'neu'}>{outcome}</Pill>
                <span className="date">{fullDateTime(o.createdAt)}</span>
              </div>
              <div className={isAgent ? 'aprow-txt flush' : 'aprow-txt'}>{o.body}</div>
              {o.resolvedAt ? (
                <div className={isAgent ? 'aprow-reply flush' : 'aprow-reply'}>
                  <b>{o.resolverName}</b>
                  <span className="when"> · {fullDateTime(o.resolvedAt)}</span>
                  {o.reply ? <><br />{o.reply}</> : null}
                </div>
              ) : null}
              <div className="aprow-act">
                <span className="btn soft sm">
                  <span>{canResolve && open ? 'İncele ve yanıtla' : 'Değerlendirmeyi aç'}</span>
                  <Icon name="chevron_right" />
                </span>
              </div>
            </div>
          );
        })}
      </Panel>
    </>
  );
}
